import type { FC, ReactNode } from 'react'
import type { Course } from '@/types'

import { User } from 'lucide-react'

import { courseTitlesSq, getCourses } from '@/constants/germa'
import { fill } from '@/i18n/config'
import { useLocale, useT } from '@/i18n'
import { breadcrumbSchema, courseSchema, graph } from '@/lib/schema'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Button } from '@/components/generals/button'
import { Reveal } from '@/components/generals/reveal'
import { Container } from '@/components/generals/container'
import { PageHeader } from '@/components/generals/page-header'
import { SectionHeading } from '@/components/generals/section-heading'
import { AnimatedHeading } from '@/components/generals/animated-heading'
import { CourseRegistrationForm } from '@/components/landing/course-registration-form'
import { CtaSection } from '@/components/landing/cta-section'

const CourseCard = ({ course, fallbackCta }: { course: Course; fallbackCta: string }) => (
    <div id={course.id} className='group flex scroll-mt-28 flex-col rounded-2xl border border-border bg-card p-7 transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_30px_60px_-30px_rgba(20,25,45,0.45)]'>
        <span className='grid size-14 place-items-center rounded-xl bg-navy/[0.05] text-navy transition-colors duration-500 group-hover:bg-navy group-hover:text-parchment'>
            <course.icon className='size-6' />
        </span>

        <h3 className='mt-6 font-display text-xl font-semibold text-foreground'>{course.title}</h3>
        <p className='mt-2 w-fit rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold-deep'>
            {course.badge ?? course.level}
        </p>
        <p className='mt-4 flex-1 text-sm leading-relaxed text-muted-foreground'>
            {course.summary ?? course.description}
        </p>

        <Button href='/kontakt' variant='ghost' size='sm' withArrow className='mt-6 self-start'>
            {course.cta ?? fallbackCta}
        </Button>
    </div>
)

const KursePage: FC = (): ReactNode => {
    const t = useT()
    const locale = useLocale()
    const c = t.courses
    const courses = getCourses(t)
    const featured = courses[0]

    return (
        <Layout>
            <Seo
                title={c.seoTitle}
                path='/kurse'
                description={c.seoDescription}
                jsonLd={graph(
                    breadcrumbSchema([{ name: c.crumb, path: '/kurse' }], locale),
                    {
                        '@type': 'ItemList',
                        name: c.listName,
                        numberOfItems: courses.length,
                        itemListElement: courses.map((course, index) => ({
                            '@type': 'ListItem',
                            position: index + 1,
                            item: courseSchema(course, locale)
                        }))
                    }
                )}
            />

            <PageHeader
                title={c.title}
                description={c.description}
                crumbs={[{ label: t.common.home, href: '/' }, { label: c.crumb }]}
            />

            <section className='py-12 md:py-16'>
                <Container>
                    <div className='grid items-center gap-12 lg:grid-cols-2 lg:gap-16'>
                        <Reveal x={-40} y={0}>
                            <div className='relative'>
                                <div className='pointer-events-none absolute -inset-3 rounded-[2rem] border border-gold/25' />
                                <div className='relative overflow-hidden rounded-[1.5rem] shadow-[0_40px_80px_-40px_rgba(20,25,45,0.6)] ring-1 ring-border'>
                                    <img
                                        src={featured.image}
                                        alt={fill(c.posterAlt, { title: featured.title })}
                                        className='h-auto w-full'
                                        loading='lazy'
                                    />
                                </div>
                            </div>
                        </Reveal>

                        <div className='flex flex-col'>
                            <AnimatedHeading
                                onScroll
                                as='h2'
                                text={featured.title}
                                className='text-3xl sm:text-4xl md:text-5xl'
                            />
                            <p className='mt-5 text-lg leading-relaxed text-muted-foreground'>
                                {featured.description}
                            </p>

                            <div className='mt-7 flex flex-wrap gap-2.5'>
                                {[featured.level, featured.format].map((chip) => (
                                    <span
                                        key={chip}
                                        className='rounded-full border border-border bg-secondary px-3.5 py-1.5 text-sm text-foreground/75'
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>

                            <div className='mt-6 flex flex-col gap-2.5 text-sm'>
                                {featured.instructor && (
                                    <div className='flex items-center gap-2.5 text-foreground/75'>
                                        <User className='size-4 text-gold-deep' />
                                        <span>
                                            <strong className='font-semibold text-foreground'>{c.teacher}</strong>{' '}
                                            {featured.instructor}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className='mt-9'>
                                <Button href='#regjistrohu' variant='gold' withArrow>
                                    {c.details}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            <section className='bg-parchment-texture py-12 md:py-16'>
                <Container>
                    <SectionHeading
                        title={c.listTitle}
                        description={c.listDescription}
                    />
                    <Reveal className='mt-14 grid gap-5 md:grid-cols-3' stagger={0.12} y={36}>
                        {courses.map((course) => (
                            <CourseCard key={course.id} course={course} fallbackCta={c.contactFallback} />
                        ))}
                    </Reveal>
                </Container>
            </section>

            <section id='regjistrohu' className='scroll-mt-24 bg-parchment-texture py-12 md:py-16'>
                <Container>
                    <div className='mx-auto grid max-w-5xl gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14'>
                        <SectionHeading
                            title={c.registerTitle}
                            description={c.registerDescription}
                        />
                        <CourseRegistrationForm
                            courses={courses.map((course) => ({
                                // The Albanian title is what gets stored, so the admin always reads one language.
                                value:
                                    course.id === 'gjuha-arabe'
                                        ? `${courseTitlesSq[course.id]} – planifikohet rifillimi`
                                        : courseTitlesSq[course.id],
                                label: course.id === 'gjuha-arabe' ? `${course.title} (${c.plannedSuffix})` : course.title
                            }))}
                        />
                    </div>
                </Container>
            </section>

            <CtaSection />
        </Layout>
    )
}

export default KursePage
